import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ChevronRight, 
  DollarSign, 
  Type, 
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { CURRENCY_OPTIONS } from '../constants';
import { BasePricingSelector } from '../components/BasePricingSelector';
import { ImagePicker } from '../../../../../shared/ImagePicker';

interface StepOneProps {
  adventureName: string;
  setAdventureName: (value: string) => void;
  adventureDescription: string;
  setAdventureDescription: (value: string) => void;
  basePrice: number;
  setBasePrice: (value: number) => void;
  basePriceType: 'fixed' | 'per_person' | 'per_day' | 'per_person_day';
  setBasePriceType: (value: 'fixed' | 'per_person' | 'per_day' | 'per_person_day') => void;
  currency: string;
  setCurrency: (value: string) => void;
  adventureImage?: string;
  setAdventureImage?: (value: string | undefined) => void;
  businessImages?: string[];
  onNext: () => void;
  onCancel: () => void;
}

export function StepOne({
  adventureName,
  setAdventureName,
  adventureDescription,
  setAdventureDescription,
  basePrice,
  setBasePrice,
  basePriceType,
  setBasePriceType,
  currency,
  setCurrency,
  adventureImage,
  setAdventureImage,
  businessImages = [],
  onNext,
  onCancel
}: StepOneProps) {
  const [nameError, setNameError] = useState<string>('');
  const [descriptionError, setDescriptionError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation
  const validateName = (value: string) => {
    if (!value.trim()) {
      setNameError('Adventure name is required');
      return false;
    }
    if (value.length < 3) {
      setNameError('Adventure name must be at least 3 characters');
      return false;
    }
    setNameError('');
    return true;
  };

  const validateDescription = (value: string) => {
    if (!value.trim()) {
      setDescriptionError('Description is required');
      return false;
    }
    if (value.length < 10) {
      setDescriptionError('Description must be at least 10 characters');
      return false;
    }
    setDescriptionError('');
    return true;
  };

  // Form validation
  const isFormValid = adventureName.trim() && 
                     adventureDescription.trim() && 
                     adventureName.length >= 3 && 
                     adventureDescription.length >= 10;

  const handleNext = async () => {
    if (!validateName(adventureName) || !validateDescription(adventureDescription)) {
      toast.error('Please fix the validation errors before continuing');
      return;
    }

    setIsSubmitting(true);
    
    // Simulate processing time for better UX
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setIsSubmitting(false);
    onNext();
  };

  const handleNameChange = (value: string) => {
    setAdventureName(value);
    if (nameError) validateName(value);
  };

  const handleDescriptionChange = (value: string) => {
    setAdventureDescription(value);
    if (descriptionError) validateDescription(value);
  };

  const currencySymbol = CURRENCY_OPTIONS.find(c => c.value === currency)?.symbol || '$';

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      
      {/* Streamlined Form - 2 Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Left Column: Basic Information */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Type className="w-4 h-4 text-blue-600" />
              Adventure Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Adventure Name */}
            <div className="space-y-2">
              <Label htmlFor="adventure-name" className="text-sm font-medium">
                Adventure Name
              </Label>
              <Input
                id="adventure-name"
                type="text"
                placeholder="e.g., African Safari Expedition"
                value={adventureName}
                onChange={(e) => handleNameChange(e.target.value)}
                onBlur={() => validateName(adventureName)}
                className={cn(
                  "focus:ring-primary focus:border-primary",
                  nameError ? "border-red-500" : ""
                )}
                maxLength={60}
              />
              {nameError && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {nameError}
                </p>
              )}
              <p className="text-xs text-slate-500">
                {adventureName.length}/60 characters
              </p>
            </div>

            {/* Adventure Description */}
            <div className="space-y-2">
              <Label htmlFor="adventure-description" className="text-sm font-medium">
                Description
              </Label>
              <Textarea
                id="adventure-description"
                placeholder="Describe what makes this adventure unique..."
                value={adventureDescription}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                onBlur={() => validateDescription(adventureDescription)}
                className={cn(
                  "focus:ring-primary focus:border-primary resize-none min-h-[100px]",
                  descriptionError ? "border-red-500" : ""
                )}
                maxLength={500}
              />
              {descriptionError && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {descriptionError}
                </p>
              )}
              <p className="text-xs text-slate-500">
                {adventureDescription.length}/500 characters
              </p>
            </div>

            {/* Adventure Image */}
            {setAdventureImage && (
              <ImagePicker
                label="Adventure Image"
                description="Choose an image to represent this custom adventure"
                selectedImage={adventureImage}
                onImageChange={setAdventureImage}
                imageLibrary={businessImages}
                placeholder="Select from your image library"
                compact={true}
                showPreview={false}
              />
            )}
          </CardContent>
        </Card>

        {/* Right Column: Pricing */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="w-4 h-4 text-purple-600" />
              Pricing Setup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Base Pricing Selector - Compact */}
            <BasePricingSelector
              selectedType={basePriceType}
              onTypeChange={setBasePriceType}
              basePrice={basePrice}
              currency={currency}
            />
            
            {/* Price and Currency */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="base-price" className="text-sm font-medium">
                  Base Price
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 font-medium">
                    {currencySymbol}
                  </span>
                  <Input
                    id="base-price"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={basePrice || ''}
                    onChange={(e) => setBasePrice(parseFloat(e.target.value) || 0)}
                    className="pl-8 focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="currency" className="text-sm font-medium">
                  Currency
                </Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger id="currency" className="focus:ring-primary focus:border-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{option.symbol}</span>
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Compact Pricing Preview */}
            {basePrice > 0 && (
              <div className="p-3 bg-slate-50 rounded-lg border">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Base Price:</span>
                  <span className="font-medium">{currencySymbol}{basePrice.toFixed(2)}</span>
                </div>
                {basePriceType !== 'fixed' && (
                  <div className="text-xs text-slate-500 mt-1">
                    {basePriceType === 'per_person' ? 'Multiplied by number of people' :
                     basePriceType === 'per_day' ? 'Multiplied by number of days' :
                     'Multiplied by people × days'}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Compact Navigation */}
      <div className="flex justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={onCancel}
          className="px-6"
        >
          Cancel
        </Button>
        <Button
          onClick={handleNext}
          disabled={!isFormValid || isSubmitting}
          className="bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 px-8 flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Processing...
            </>
          ) : (
            <>
              Continue to Options
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
} 